import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Typography, Autocomplete } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import PropTypes from 'prop-types';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { visuallyHidden } from '@mui/utils';
import DownloadIcon from '@mui/icons-material/Download';
import { CsvBuilder } from "filefy";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getTransactionByContract, getTransactionByMerchant } from "../../services/Transaction";
import { getAllMerchant } from "../../services/Merchant";
import { getAllTerminal } from "../../services/Terminal";
import Color from "../layout/color/Color";
import FontSize from "../layout/FontSize";

const backButton = {
    bgcolor: Color.Button,
    borderRadius: '30px',
    border: "1px solid #055C2D",
    color: Color.Main,
    fontWeight: '500',
    textAlign: 'center',
    padding: '4px 8px',
    transition: '0.3s',
    fontSize: FontSize.Medium,
    textTransform: 'none',
    '&:hover': { background: Color.Main, color: Color.Button }
  };

const headCellDetails = [
    {
        label: 'ID',
        id: 'id',
        disablePadding: false,
    },
    {
        label: 'Type',
        id: 'transtypeid',
        disablePadding: false,
    },
    {
        label: 'Amount',
        id: 'grossamount',
        disablePadding: false,
    },
    {
        label: 'Fee Amount',
        id: 'feeamount',
        disablePadding: false,
    },
    {
        label: 'Net Amount',
        id: 'netamount',
        disablePadding: false,
    },
    {
        label: 'Status',
        id: 'transactionstatus',
        disablePadding: false,
    },
    {
        label: 'Bank',
        id: 'issbank',
        disablePadding: false,
    },
    {
        label: 'Card number',
        id: 'targetcontractorig',
        disablePadding: false,
    },
    {
        label: 'Trans Date',
        id: 'transdate',
        disablePadding: false,
    },
    {
        label: 'Payment Date',
        id: 'paymentdate',
        disablePadding: false,
    },
]

const headCells = [
    {
        label: 'Amount',
        id: 'grossamount',
        disablePadding: false,
    },
    {
        label: 'Fee Amount',
        id: 'feeamount',
        disablePadding: false,
    },
    {
        label: 'Net Amount',
        id: 'netamount',
        disablePadding: false,
    },
    {
        label: 'Source',
        id: 'cardips',
        disablePadding: false,
    },
    {
        label: 'Create At',
        id: 'transdate',
        disablePadding: false,
    },
    {
        label: 'Action',
        id: 'action',
        disablePadding: false,
    }
]
const headCellExports = [
    {
        label: 'Amount',
        id: 'grossamount',
        disablePadding: false,
    },
    {
        label: 'Fee Amount',
        id: 'feeamount',
        disablePadding: false,
    },
    {
        label: 'Net Amount',
        id: 'netamount',
        disablePadding: false,
    },
    {
        label: 'Source',
        id: 'cardips',
        disablePadding: false,
    },
    {
        label: 'Create At',
        id: 'transdate',
        disablePadding: false,
    },
]

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}
function BasicTable(props) {
    const { order, orderBy, onRequestSort } =
        props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                <TableCell>
                </TableCell>
                {headCells.map((headCell, index) => (
                    <TableCell
                        key={headCell.id}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

BasicTable.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const totalTransactionData = (data) => {
    let castData = [];
    const initValue = {
        grossamount: 0,
        feeamount: 0,
        netamount: 0
    }
    if (data !== undefined) castData = data.map(item => {
        return {
            grossamount: parseInt(item.grossamount),
            feeamount: parseInt(item.feeamount),
            netamount: parseInt(item.netamount)
        }
    })
    const totalData = castData.reduce((pre, cur) => {
        pre.grossamount += cur.grossamount;
        pre.feeamount += cur.feeamount;
        pre.netamount += cur.netamount;
        return pre;
    }, initValue);
    return totalData;
}

const TransactionDetailButton = ({ record }) => {
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    return (
        <>
            <Button variant='outlined' startIcon={<EditIcon />} onClick={handleOpen}>
                View
            </Button>
            <Dialog onClose={handleClose} open={open} maxWidth="md">
                <DialogTitle sx={{ m: 0, p: 2 }}>
                    Detail Transaction
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500]
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        {headCellDetails.map((p, index) => {
                            return (
                                <Grid item xs={6} key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant='subtitle1' fontWeight='bold' sx={{ mr: 1 }}>{p.label}: </Typography>
                                    <Typography variant='subtitle1'>{record[p.id]}</Typography>
                                </Grid>
                            )
                        })}
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    )
}

export const TransactionTable = () => {
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('grossamount');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [count, setCount] = useState(0);
    const [transactions, setTransactions] = useState([]);
    const [startDate, setStartDate] = useState(new Date((new Date()).getTime() - 30 * 24 * 60 * 60 * 1000));
    const [endDate, setEndDate] = useState(new Date());
    const [merchant, setMerchant] = useState(null);
    const [terminal, setTerminal] = useState(null);
    const [merchants, setMerchants] = useState([]);
    const [terminals, setTerminals] = useState([]);

    const userRole = useMemo(() => {
        return parseInt(localStorage.getItem('userRole'));
    }, [])

    const totalData = useMemo(() => {
        return totalTransactionData(transactions);
    }, [transactions])

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        const fetchAPI = async () => {
            try {
                let response;
                if (userRole === 1) {
                    response = await getTransactionByMerchant(merchant === null ? null : merchant.id, startDate, endDate, page, rowsPerPage, orderBy, order);
                }
                else {
                    response = await getTransactionByContract(terminal === null ? null : terminal.id, startDate, endDate, page, rowsPerPage, orderBy, order);
                }
                if (response !== null && response !== undefined) {
                    setTransactions(response);
                    setCount(response.length);
                }
            } catch (err) {
                console.log(err);
            }
        }
        fetchAPI();
    }, [startDate, endDate, page, rowsPerPage, orderBy, order, merchant, terminal, userRole])

    useEffect(() => {
        const fetchAPI = async () => {
            try {
                if (userRole === 1) {
                    const response = await getAllMerchant();
                    if (response !== null && response !== undefined) setMerchants(response);
                }
                else {
                    const response = await getAllTerminal();
                    if (response !== null && response !== undefined) setTerminals(response);
                }
            } catch (err) {
                console.log(err);
            }
        }

        fetchAPI();
    }, [userRole])

    const handleExportCSV = (e) => {
        const rows = transactions.map((transaction) => {
            return headCellExports.map((headCell) => transaction[headCell.id])
        })
        e.preventDefault();
        new CsvBuilder("transaction.csv")
            .setColumns(headCellExports.map((headCell) => headCell.label))
            .addRows(rows) 
            .addRow([`${totalData.grossamount}`, `${totalData.feeamount}`, `${totalData.netamount}`, `Total`])
            .exportFile();
    }

    return (
        <>
            <Grid container spacing={4} sx={{ alignItems: 'center' }}>
                <Grid item xs={12} md={3}>
                    {userRole === 1 ? (
                        <Autocomplete
                            options={merchants}
                            value={merchant}
                            getOptionLabel={(option) => option.name}
                            onChange={(e, newValue) => setMerchant(newValue)}
                            renderInput={(params) => <TextField {...params} label="Merchant" />}
                        />
                    ) : (<Autocomplete
                        options={terminals}
                        value={terminal}
                        getOptionLabel={(option) => option.alias}
                        onChange={(e, newValue) => setTerminal(newValue)}
                        renderInput={(params) => <TextField {...params} label="Terminal" />}
                    />
                    )}
                </Grid>
                <Grid item xs={12} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'end' }}>
                    <Button sx={backButton} startIcon={<DownloadIcon />} onClick={handleExportCSV}>Export</Button>
                </Grid>
            </Grid>
            <TableContainer>
                <Table aria-labelledby="tableTitle">
                    <BasicTable
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        rowCount={count}
                    />
                    <TableBody>
                        {stableSort(transactions, getComparator(order, orderBy))
                            ?.map((row, index) => {
                                return (
                                    <TableRow
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        key={row.id}
                                    >
                                        <TableCell></TableCell>
                                        {headCells.map((p, index) => {
                                            return p.id === 'action' ? (
                                                <TableCell key={index}>
                                                    <TransactionDetailButton record={row} />
                                                </TableCell>
                                            ) : (
                                                <TableCell key={index} sx={{ minWidth: '12em' }}>{row[p.id]}</TableCell>
                                            )
                                        })}
                                    </TableRow>
                                );
                            })}
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell
                                sx={{ minWidth: '12em', fontWeight: 'bold' }}
                            >
                                {totalData.grossamount}
                            </TableCell>
                            <TableCell
                                sx={{ minWidth: '12em', fontWeight: 'bold' }}
                            >
                                {totalData.feeamount}
                            </TableCell>
                            <TableCell
                                sx={{ minWidth: '12em', fontWeight: 'bold' }}
                            >
                                {totalData.netamount}
                            </TableCell>
                            <TableCell
                                colSpan={3}
                                sx={{ textAlign: 'center', fontWeight: 'bold' }}
                            >
                                Total
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={count}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </>
    )
}